// ============================================================================
// permissions.ts — Pure utility module for permission resolution
// ============================================================================
// No React imports — this is independently testable business logic.
// Called by AuthContext but decoupled from any framework.
//
// 3-level access tier system: none / view / edit.
//
// RBAC Slice E (2026-05-12) DROPPED the per-section 'admin' tier. Admin-ness
// is now a role-level concept (`platform_roles.is_platform_admin` flag),
// not a per-section tier. Any 'admin' strings still present in legacy DB
// `permissions` JSONB get NORMALIZED to 'edit' at the boundary — they keep
// granting edit access on the section, and the role-level admin flag (set
// during the Slice D backfill) is what gates admin-only features.
//
// Backward compatible with legacy boolean permissions via normalizePermissionValue.
// ============================================================================

/**
 * Access level hierarchy (ordered lowest → highest).
 *
 * Slice E note: 'admin' is no longer a section-level tier. Admin-ness is
 * a role-level concept (`is_platform_admin`). DB rows with 'admin' values
 * are normalized to 'edit' on read.
 */
export type AccessLevel = 'none' | 'view' | 'edit';

/** Numeric rank for access level comparison */
const ACCESS_RANK: Record<AccessLevel, number> = {
  none: 0,
  view: 1,
  edit: 2,
};

/**
 * Permission value type — supports both legacy booleans and new tier strings.
 */
export type PermissionValue = boolean | string;

/**
 * Platform role info as stored in AuthContext.
 * permissions values can be booleans (legacy) or AccessLevel strings (new).
 */
export interface PlatformRoleInfo {
  roleId: string;
  roleName: string;
  permissions: Record<string, PermissionValue>;
}

/**
 * Override info — a platform role granted on top of the base role
 */
export interface PlatformOverrideInfo extends PlatformRoleInfo {
  overrideReason: string | null;
  expiresAt: string | null;
}

// ─── Core Tier Utilities ─────────────────────────────────────────────────────

/**
 * Normalize a permission value (boolean or string) to an AccessLevel.
 *
 * Mapping:
 * - `true`          → 'edit'  (legacy boolean = had access = edit-level)
 * - `false`         → 'none'
 * - `undefined`     → 'none'
 * - `'view'`        → 'view'
 * - `'edit'`        → 'edit'
 * - `'admin'`       → 'edit'  (Slice E: legacy admin tier normalized down)
 * - `'none'`        → 'none'
 * - anything else   → 'none'
 */
export function normalizePermissionValue(value: PermissionValue | undefined): AccessLevel {
  if (value === undefined || value === null) return 'none';
  if (typeof value === 'boolean') return value ? 'edit' : 'none';
  if (value === 'view' || value === 'edit' || value === 'none') return value;
  // RBAC Slice E: legacy 'admin' DB values get downgraded to 'edit' at the
  // boundary. Admin-ness lives at the role level (is_platform_admin flag),
  // not on individual sections.
  if (value === 'admin') return 'edit';
  return 'none';
}

/**
 * Check if an actual access level meets a minimum required level.
 *
 * Hierarchy: none(0) < view(1) < edit(2) < admin(3)
 */
export function accessLevelMeetsMinimum(actual: AccessLevel, required: AccessLevel): boolean {
  return ACCESS_RANK[actual] >= ACCESS_RANK[required];
}

/**
 * Get the effective access level for a section from a permissions record.
 * Handles the empty-permissions = full-admin convention.
 */
export function getEffectiveLevel(
  permissions: Record<string, PermissionValue>,
  sectionKey: string
): AccessLevel {
  // Empty permissions = full access role (e.g., "Admin" role with no
  // restrictions). Slice E: section-level returns 'edit' (highest section
  // tier post-admin-removal). Role-level admin-ness is checked separately
  // via the `is_platform_admin` flag.
  if (Object.keys(permissions).length === 0) return 'edit';
  return normalizePermissionValue(permissions[sectionKey]);
}

// ─── Permission Check Functions ──────────────────────────────────────────────

/**
 * Check if user can VIEW a specific section within a platform.
 *
 * - Empty permissions object → full access (admin) → can view
 * - Tier value >= 'view' → can view
 * - Legacy boolean `true` → normalized to 'edit' → can view
 */
export function canViewSection(
  permissions: Record<string, PermissionValue>,
  sectionKey: string
): boolean {
  const level = getEffectiveLevel(permissions, sectionKey);
  return accessLevelMeetsMinimum(level, 'view');
}

/**
 * Check if user can EDIT a specific section within a platform.
 *
 * - Empty permissions object → full access (admin) → can edit
 * - Tier value >= 'edit' → can edit
 * - Legacy boolean `true` → normalized to 'edit' → can edit
 * - Tier value 'view' → cannot edit
 */
export function canEditSection(
  permissions: Record<string, PermissionValue>,
  sectionKey: string
): boolean {
  const level = getEffectiveLevel(permissions, sectionKey);
  return accessLevelMeetsMinimum(level, 'edit');
}

/**
 * DEPRECATED — RBAC Slice E (2026-05-12). Use the role-level
 * `platform_roles.is_platform_admin` flag via
 * `useAuth().isAdminForPlatform(platform)` instead.
 *
 * This utility inferred admin-ness from the permissions JSONB shape
 * (empty {} or any section with 'admin' tier). With Slice E the per-
 * section 'admin' tier is gone and the flag is the source of truth.
 * Kept here only for any niche caller that still wants permissions-shape
 * inference; runtime returns the legacy-equivalent answer.
 */
export function isAdminForPlatform(
  permissions: Record<string, PermissionValue>
): boolean {
  // Empty permissions = full access role = admin
  if (Object.keys(permissions).length === 0) return true;
  // Legacy 'admin' string still detectable via raw value comparison
  // (normalize would downgrade it to 'edit').
  return Object.values(permissions).some(
    v => v === 'admin'
  );
}

/**
 * Merge multiple permissions records by taking the HIGHEST access level per
 * section. Used to aggregate split platforms (e.g. `recruiting-md` +
 * `recruiting-crna` → a synthetic `recruiting` entry) so that existing
 * section-level checks like `canEdit('recruiting', 'recruits')` keep working.
 *
 * Empty permissions in any source are treated as 'admin' (the full-access
 * convention), matching `isAdminForPlatform`.
 *
 * Returns a Record where each key is the max-level across inputs, expressed
 * as a tier string ('view'|'edit'|'admin').
 */
export function mergePermissions(
  sources: Array<Record<string, PermissionValue>>
): Record<string, PermissionValue> {
  // If any source is the empty-object "full admin" role, the merged result is
  // also full admin (empty object, to match convention).
  if (sources.some(src => Object.keys(src).length === 0)) {
    return {};
  }

  const result: Record<string, AccessLevel> = {};
  for (const src of sources) {
    for (const [key, value] of Object.entries(src)) {
      const level = normalizePermissionValue(value);
      if (level === 'none') continue;
      const existing = result[key];
      if (!existing || ACCESS_RANK[level] > ACCESS_RANK[existing]) {
        result[key] = level;
      }
    }
  }
  return result;
}

/**
 * Merge base platform roles with overrides.
 * Override takes priority — if both base and override exist for the same
 * platform, the override wins.
 *
 * Returns a merged record of platform → PlatformRoleInfo
 */
export function mergeRolesWithOverrides(
  baseRoles: Record<string, PlatformRoleInfo>,
  overrides: Record<string, PlatformRoleInfo>
): Record<string, PlatformRoleInfo> {
  const merged = { ...baseRoles };

  for (const [platform, override] of Object.entries(overrides)) {
    // Override replaces base role for this platform
    merged[platform] = override;
  }

  return merged;
}

/**
 * Resolve the effective access level for a section given base roles + overrides.
 * The override takes priority if present.
 *
 * @returns boolean — whether the user can view the section
 */
export function resolveEffectiveSectionAccess(
  platform: string,
  sectionKey: string,
  baseRoles: Record<string, PlatformRoleInfo>,
  overrides: Record<string, PlatformRoleInfo>
): boolean {
  // Check override first (takes priority)
  const override = overrides[platform];
  if (override) {
    return canViewSection(override.permissions, sectionKey);
  }

  // Fall back to base role
  const baseRole = baseRoles[platform];
  if (baseRole) {
    return canViewSection(baseRole.permissions, sectionKey);
  }

  // No role at all for this platform
  return false;
}
